/**
 * UserStatusModal
 * Modal de confirmação para inativar ou reativar um usuário (médico ou paciente)
 */
import { useState } from 'react';
import { AlertTriangle, UserCheck, UserX, Loader2, X } from 'lucide-react';

interface UserStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
  userName: string;
  action: 'suspend' | 'reactivate';
}

export function UserStatusModal({
  isOpen,
  onClose,
  onConfirm,
  userName,
  action,
}: UserStatusModalProps) {
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const isSuspend = action === 'suspend';

  const handleConfirm = async () => {
    if (isSuspend && !reason.trim()) return;
    setIsLoading(true);
    try {
      await onConfirm(reason.trim() || 'Reativação administrativa');
      setReason('');
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 w-full max-w-md shadow-2xl overflow-hidden">
        {/* Header */}
        <div
          className={`p-5 flex items-center justify-between border-b border-gray-100 dark:border-neutral-800 ${
            isSuspend
              ? 'bg-red-50 dark:bg-red-950/20'
              : 'bg-green-50 dark:bg-green-950/20'
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isSuspend
                  ? 'bg-red-100 dark:bg-red-900/30 text-red-600'
                  : 'bg-green-100 dark:bg-green-900/30 text-green-600'
              }`}
            >
              {isSuspend ? <UserX className="w-5 h-5" /> : <UserCheck className="w-5 h-5" />}
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900 dark:text-white">
                {isSuspend ? 'Inativar Usuário' : 'Reativar Usuário'}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">{userName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1 rounded-lg hover:bg-white/50 dark:hover:bg-neutral-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {isSuspend && (
            <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-800/30 rounded-xl">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 dark:text-amber-300">
                O usuário perderá acesso imediato à plataforma. Essa ação é auditada. Se o usuário for um
                médico, consultas agendadas poderão ser afetadas.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {isSuspend ? 'Motivo da inativação *' : 'Motivo da reativação'}
            </label>
            <textarea
              placeholder={
                isSuspend
                  ? 'Descreva o motivo da inativação (obrigatório)...'
                  : 'Opcional — descreva o motivo da reativação...'
              }
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full p-3 text-sm bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl resize-none outline-none focus:ring-2 focus:ring-teal-500 dark:text-white"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 dark:bg-neutral-800/50 border-t border-gray-100 dark:border-neutral-800 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading || (isSuspend && !reason.trim())}
            className={`px-4 py-2 text-sm font-bold text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
              isSuspend
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            {isSuspend ? 'Confirmar Inativação' : 'Confirmar Reativação'}
          </button>
        </div>
      </div>
    </div>
  );
}
